import React, { forwardRef, useImperativeHandle, useRef } from "react";
import { faMultiply, faSearch } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import styles from "../styles/nonexpandablesearchbox.module.css";

const joinClasses = (...classNames) => classNames.filter(Boolean).join(" ");

const NonExpandableSearchBox = forwardRef(({
    searchTerm,
    setSearchTerm,
    onChange,
    onClear,
    clearOnEscape = true,
    showSearchIcon = true,
    showClearButton = true,
    clearButtonLabel = "Clear search",
    searchIcon = faSearch,
    clearIcon = faMultiply,
    className,
    containerClassName,
    inputClassName,
    iconClassName,
    searchIconClassName,
    clearIconClassName,
    clearButtonClassName,
    containerStyle,
    inputStyle,
    iconStyle,
    searchIconStyle,
    clearIconStyle,
    style = null,
    disabled = false,
    autoComplete = "off",
    type = "search",
    onKeyDown,
    ...props
}, forwardedRef) => {
    const inputRef = useRef(null);
    const currentValue = props.value ?? searchTerm ?? "";
    const ariaLabel = props["aria-label"];
    const ariaLabelledBy = props["aria-labelledby"];
    const ariaKeyShortcuts = props["aria-keyshortcuts"];
    const legacyInputStyle = style ? { ...style } : undefined;
    const legacyIconColorStyle = style?.color ? { color: style.color } : undefined;

    useImperativeHandle(forwardedRef, () => inputRef.current);

    const updateValue = (nextValue) => {
        setSearchTerm?.(nextValue);
    };

    const focusInput = () => {
        inputRef.current?.focus();
    };

    const handleClear = () => {
        if (disabled) {
            return;
        }

        updateValue("");
        onClear?.();
        focusInput();
    };

    const handleKeyDown = (event) => {
        onKeyDown?.(event);

        if (
            event.defaultPrevented ||
            !clearOnEscape ||
            event.key !== "Escape" ||
            !currentValue
        ) {
            return;
        }

        event.preventDefault();
        handleClear();
    };

    const handleChange = (event) => {
        onChange?.(event);

        if (!onChange) {
            updateValue(event.target.value);
        }
    };

    const isClearable = showClearButton && !!currentValue && (!!setSearchTerm || !!onClear);

    return (
        <div
            className={joinClasses(
                styles.container,
                disabled && styles.disabled,
                className,
                containerClassName
            )}
            style={containerStyle}
            data-has-value={currentValue ? "true" : "false"}
        >
            {showSearchIcon ? (
                <span
                    className={joinClasses(
                        styles.icon,
                        styles.searchIcon,
                        iconClassName,
                        searchIconClassName
                    )}
                    style={{ ...legacyIconColorStyle, ...iconStyle, ...searchIconStyle }}
                    aria-hidden="true"
                >
                    <FontAwesomeIcon icon={searchIcon} />
                </span>
            ) : null}

            <input
                {...props}
                ref={inputRef}
                type={type}
                autoComplete={autoComplete}
                value={currentValue}
                disabled={disabled}
                aria-label={ariaLabelledBy ? undefined : ariaLabel ?? props.placeholder ?? "Search"}
                aria-keyshortcuts={ariaKeyShortcuts ?? (clearOnEscape ? "Escape" : undefined)}
                className={joinClasses(styles.input, inputClassName)}
                style={{ ...legacyInputStyle, ...inputStyle }}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
            />

            {isClearable ? (
                <button
                    type="button"
                    className={joinClasses(styles.clearButton, clearButtonClassName)}
                    onClick={handleClear}
                    aria-label={clearButtonLabel}
                    title={clearButtonLabel}
                    disabled={disabled}
                >
                    <span
                        className={joinClasses(
                            styles.icon,
                            styles.clearIcon,
                            iconClassName,
                            clearIconClassName
                        )}
                        style={{ ...legacyIconColorStyle, ...iconStyle, ...clearIconStyle }}
                        aria-hidden="true"
                    >
                        <FontAwesomeIcon icon={clearIcon} />
                    </span>
                </button>
            ) : null}
        </div>
    );
});

NonExpandableSearchBox.displayName = "NonExpandableSearchBox";

export default NonExpandableSearchBox;
